/**
 * Type definitions for FinTrack application
 * Aligns with backend models and API responses
 */

export interface User {
  id: string;
  email: string;
  firstName: string;
  fullName: string;
  lastName: string;
  profilePicture?: string;
  isEmailVerified?: boolean;
  mfaEnabled?: boolean;
  subscriptionTier: 'free' | 'premium';
  authProviders?: string[];
  preferences?: UserPreferences;
  createdAt: string;
  updatedAt: string;
}

export type FocusPageKey = 'dashboard' | 'transactions' | 'review' | 'analytics';

export interface PerPageFocusModes {
  dashboard?: string;
  transactions?: string;
  review?: string;
  analytics?: string;
}

export interface FocusPresetProfile {
  id: string;
  name: string;
  perPageFocusModes?: PerPageFocusModes;
  lastAppliedAt?: string | null;
}

export interface UserPreferences {
  currency?: string;
  dateFormat?: string;
  budgetMethod?: string;
  theme?: string;
  timezone?: string;
  // Insights panel preferences [T70P2-b]
  insightsGroupBy?: string;
  insightsTimeframe?: string;
  insightsDashboardLayout?: string[];
  showLineItemsInList?: boolean;
  auditRetentionDays?: number;
  activePresetId?: string | null;
  presetProfiles?: FocusPresetProfile[];
  perPageFocusModes?: PerPageFocusModes;
  // T146P2: Item display settings
  itemDisplayDensity?: 'compact' | 'receipt' | 'detailed';
  itemColumnsByPage?: Record<string, Record<string, boolean>>;
  itemColumns?: Record<string, boolean>;
  automation?: {
    autoCategorize?: boolean;
    showSuggestions?: boolean;
    requirePreview?: boolean;
  };
  notifications?: {
    emailNotifications?: boolean;
    budgetAlerts?: boolean;
    billReminders?: boolean;
  };
}

export interface Session {
  id: string;
  deviceInfo?: {
    browser?: string;
    os?: string;
    deviceType?: string;
    userAgent?: string;
  };
  ipAddress?: string;
  lastUsed: string;
  createdAt: string;
}

export interface SecurityEvent {
  eventType: string;
  ipAddress: string;
  userAgent?: string;
  success: boolean;
  message?: string;
  timestamp: string;
}

// [T114-F3] Timezone types
export interface TimezoneInfo {
  name: string;
  label: string;
  offset: string;
}

export interface TimezoneGroup {
  region: string;
  zones: TimezoneInfo[];
}

export interface Account {
  id: string;
  userId: string;
  name: string;
  accountType: 'checking' | 'savings' | 'credit_card' | 'investment' | 'loan' | 'mortgage' | 'manual' | 'crypto' | 'other';
  balance: number;
  availableBalance?: number;
  creditLimit?: number;
  currency: string;
  institution?: string;
  institutionName?: string;
  accountNumber?: string;
  isActive: boolean;
  plaidAccountId?: string;
  plaidAccessToken?: string;
  plaidItemId?: string;
  lastSyncedAt?: string;
  syncStatus?: 'success' | 'error' | 'pending' | 'never';
  notes?: string;
  color?: string;
  emoji?: string;
  isManual?: boolean;
  interestRate?: number;
  createdAt: string;
  updatedAt: string;
}

/** Populated category object returned by some API endpoints */
export interface TransactionCategory {
  _id?: string;
  id?: string;
  name?: string;
  color?: string;
  emoji?: string;
}

export interface Transaction {
  id: string;
  userId: string;
  accountId: string;
  type: 'income' | 'expense' | 'transfer';
  amount: number;
  discountAmount?: number;
  date: string;
  description: string;
  /** String category ID or name, as returned by most endpoints */
  category?: string;
  categoryId?: string;
  currency?: string;
  goal?: string;
  merchant?: string;
  location?: string;
  notes?: string;
  tags?: string[];
  isRecurring?: boolean;
  emoji?: string;
  recurringPattern?: {
    frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly';
    interval?: number;
    isActive?: boolean;
    nextDate?: string;
    endDate?: string;
  };
  isPending?: boolean;
  isSplit?: boolean;
  splitTransactions?: SplitTransaction[];
  /** T80P1: Line items for itemized transactions */
  items?: TransactionItem[];
  hasItems?: boolean;
  receiptAttachment?: {
    filename: string;
    path: string;
    mimetype: string;
    size: number;
  };
  plaidTransactionId?: string;
  linkedTransactionId?: string;
  /** T155P1: Triage flag — true means transaction needs human review */
  needsReview?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SplitTransaction {
  category: string;
  amount: number;
  notes?: string;
}

/** T80P1: Line item within a transaction */
export interface TransactionItem {
  description: string;
  amount: number;
  discountAmount?: number;
  quantity?: number;
  category?: string;
  categoryId?: string;
  categoryName?: string;
  categoryColor?: string;
  categoryEmoji?: string;
  notes?: string;
  emoji?: string;
}

export interface Category {
  id: string;
  userId: string;
  name: string;
  type: 'income' | 'expense';
  description?: string;
  color?: string;
  icon?: string;
  emoji?: string;
  parentCategoryId?: string;
  isDefault?: boolean;
  budget?: number;
  createdAt: string;
  updatedAt: string;
}

/** [T84P2] Category stats returned by GET /categories/:id/stats */
export interface CategoryStats {
  category: Category;
  thisMonth: { total: number; count: number };
  allTime: { total: number; count: number };
  /** month = 'YYYY-MM' */
  last6Months: Array<{ month: string; total: number }>;
  recentTransactions: Array<{
    id: string;
    description: string;
    amount: number;
    date: string;
    type: 'income' | 'expense' | 'transfer';
    emoji?: string;
  }>;
}

export interface Budget {
  id: string;
  userId: string;
  name: string;
  year: number;
  month: number;
  budgetMethod: 'zero-based' | 'spending-plan' | 'rollover';
  categories: CategoryBudget[];
  income: {
    planned: number;
    actual: number;
  };
  // Computed totals from backend enrichment
  totalBudgeted?: number;
  totalSpent?: number;
  totalRemaining?: number;
  overallPercentage?: number;
  plannedIncome?: number;
  allowRollover?: boolean;
  notes?: string;
  emoji?: string;
  color?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryBudget {
  categoryId: string;
  categoryName: string;
  categoryIcon?: string;
  categoryColor?: string;
  budgeted: number;
  spent: number;
  rollover?: number;
  remaining?: number;
  percentage?: number;
}

// T68P1-c: Offline sync queue item
export interface SyncQueueItem {
  id: string;           // crypto.randomUUID()
  resource: 'transactions' | 'budgets' | 'goals' | 'accounts' | 'categories';
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  payload: Record<string, unknown>;
  optimisticId?: string; // temp UUID used in Redux state
  timestamp: number;     // Date.now()
  retries: number;
  maxRetries: number;    // default 3
}

export interface Goal {
  id: string;
  userId: string;
  name: string;
  type: 'savings' | 'debt_payoff' | 'investment' | 'other';
  targetAmount: number;
  currentAmount: number;
  targetDate?: string;
  priority: number;
  linkedAccountId?: string;
  status: 'active' | 'completed' | 'paused';
  notes?: string;
  emoji?: string;
  color?: string;
  debtPayoffDetails?: {
    originalBalance: number;
    interestRate: number;
    payoffStrategy: 'avalanche' | 'snowball';
  };
  milestones?: GoalMilestone[];
  createdAt: string;
  updatedAt: string;
}

export interface GoalMilestone {
  amount: number;
  date: string;
  achieved: boolean;
}

export interface UpcomingBill {
  name: string;
  amount: number;
  dueDate: string;
  category?: string;
  accountId?: string;
  isPaid?: boolean;
}

export interface Bill {
  _id: string;
  user: string;
  name: string;
  description?: string;
  amount: number;
  currency: string;
  dueDate: string;
  category?: string;
  account?: string;
  status: 'upcoming' | 'paid' | 'overdue' | 'cancelled';
  isRecurring: boolean;
  recurringPattern?: {
    frequency: 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly';
    interval: number;
    endDate?: string;
  };
  isPaid: boolean;
  paidDate?: string;
  paidAmount?: number;
  linkedTransactionId?: string;
  reminderDays: number;
  emoji?: string;
  notes?: string;
  tags: string[];
  paymentHistory: Array<{ amount: number; date: string; transactionId?: string }>;
  autoPayEnabled: boolean;
  isOverdue: boolean;
  daysUntilDue: number;
  createdAt: string;
  updatedAt: string;
}

export interface BillSummary {
  totalUpcoming: number;
  totalOverdue: number;
  paidThisMonth: number;
  recurringMonthlyTotal: number;
  upcomingCount: number;
  overdueCount: number;
  paidCount: number;
}

// Chart data types
export interface NetWorthDataPoint {
  date: string;
  assets: number;
  liabilities: number;
  netWorth: number;
}

export interface CashFlowDataPoint {
  month: string;
  income: number;
  expenses: number;
  netCashFlow: number;
}

export interface SpendingByCategory {
  category: string;
  amount: number;
  percentage: number;
  color?: string;
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface ApiError {
  success: false;
  error: {
    message: string;
    code?: string;
    details?: unknown;
  };
}

// ─── Data Portability Types ───────────────────────────────────────────────────

export enum DataScope {
  TRANSACTIONS = 'transactions',
  BUDGETS = 'budgets',
  GOALS = 'goals',
  ACCOUNTS = 'accounts',
  CATEGORIES = 'categories',
}

export const ALL_DATA_SCOPES: DataScope[] = [
  DataScope.ACCOUNTS,
  DataScope.CATEGORIES,
  DataScope.TRANSACTIONS,
  DataScope.BUDGETS,
  DataScope.GOALS,
];

export const DATA_SCOPE_LABELS: Record<DataScope, string> = {
  [DataScope.TRANSACTIONS]: 'Transactions',
  [DataScope.BUDGETS]: 'Budgets',
  [DataScope.GOALS]: 'Goals',
  [DataScope.ACCOUNTS]: 'Accounts',
  [DataScope.CATEGORIES]: 'Categories',
};

export const DATA_SCOPE_DESCRIPTIONS: Record<DataScope, string> = {
  [DataScope.TRANSACTIONS]: 'All transactions + their categories (auto-included)',
  [DataScope.BUDGETS]: 'All budgets + their categories (auto-included)',
  [DataScope.GOALS]: 'All financial goals',
  [DataScope.ACCOUNTS]: 'All bank/financial accounts',
  [DataScope.CATEGORIES]: 'All spending/income categories',
};

export enum ConflictStrategy {
  SKIP = 'skip',
  OVERWRITE = 'overwrite',
  DUPLICATE = 'duplicate',
}

export interface ExportMetadata {
  schemaVersion: string;
  format: string;
  exportedAt: string;
  exportedBy: string;
  scopes: DataScope[];
  dateRange?: { from?: string; to?: string };
  counts: Record<string, number>;
  appVersion?: string;
}

export interface ExportData {
  accounts?: Record<string, unknown>[];
  categories?: Record<string, unknown>[];
  transactions?: Record<string, unknown>[];
  budgets?: Record<string, unknown>[];
  goals?: Record<string, unknown>[];
}

export interface ExportFile {
  metadata: ExportMetadata;
  data: ExportData;
}

export interface ImportOptions {
  scopes: DataScope[];
  conflictStrategy: ConflictStrategy;
  dateFrom?: string;
  dateTo?: string;
}

export interface ScopeImportResult {
  scope: DataScope;
  created: number;
  skipped: number;
  overwritten: number;
  errors: string[];
}

export interface ImportResult {
  success: boolean;
  importedAt: string;
  scopes: ScopeImportResult[];
  refMap: Record<string, string>;
}

export interface ImportValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  metadata: ExportMetadata | null;
  availableScopes: DataScope[];
  counts: {
    accounts?: number;
    categories?: number;
    transactions?: number;
    budgets?: number;
    goals?: number;
  };
}

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

// ─── Analytics Types ──────────────────────────────────────────────────────────

export interface SafeToSpendData {
  totalIncome: number;
  fixedExpenses: number;
  goalContributions: number;
  safeToSpend: number;
  dailyBudget: number;
  remainingDays: number;
  currency: string;
}

export interface RunwayData {
  liquidBalance: number;
  monthlyBurnRate: number;
  runwayMonths: number;
  runwayDays: number;
  currency: string;
}

export interface NetWorthData {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  history: Array<{
    date: string;
    totalAssets: number;
    totalLiabilities: number;
    netWorth: number;
  }>;
  currency: string;
}

export interface CalendarForecastItem {
  date: string;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  isRecurring: boolean;
  category?: string;
}

export interface AnomalyItem {
  transactionId: string;
  description: string;
  amount: number;
  expectedAmount: number;
  deviation: number;
  merchant: string;
  date: string;
  type: 'high_amount' | 'duplicate' | 'unusual_merchant';
}

export interface DebtPayoffPlan {
  strategy: 'avalanche' | 'snowball';
  totalDebt: number;
  monthlyPayment: number;
  monthsToPayoff: number;
  totalInterest: number;
  schedule: Array<{
    month: number;
    accountName: string;
    payment: number;
    principal: number;
    interest: number;
    remainingBalance: number;
  }>;
}

export interface SpendingTrend {
  period: string;
  total: number;
  categories: Record<string, number>;
}

export interface TaxSummaryData {
  totalIncome: number;
  totalDeductible: number;
  estimatedTax: number;
  effectiveRate: number;
  categories: Array<{
    name: string;
    amount: number;
    isDeductible: boolean;
  }>;
}

// ─── Analytics Overview & Spending Trends (T99P1) ─────────────────────────────

export interface AnalyticsOverviewData {
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
  savingsRate: number;
  transactionCount: number;
  topCategories: Array<{
    category: string;
    categoryId?: string;
    amount: number;
    percentage: number;
    color?: string;
  }>;
  accountBalances: Array<{
    accountId: string;
    accountName: string;
    accountType: string;
    balance: number;
    currency: string;
  }>;
  dateRange: { startDate: string; endDate: string };
  previousPeriod?: {
    totalIncome: number;
    totalExpenses: number;
    netIncome: number;
    savingsRate: number;
  };
}

export interface SpendingTrendMonth {
  month: string;
  income: number;
  expenses: number;
  net: number;
  savingsRate: number;
}

// ─── Subscription Types ───────────────────────────────────────────────────────

export interface SubscriptionSummary {
  totalMonthly: number;
  totalAnnual: number;
  activeCount: number;
  pausedCount: number;
  upcomingRenewals: Array<{
    id: string;
    name: string;
    amount: number;
    currency: string;
    nextBillingDate: string;
    emoji?: string;
  }>;
  recentPriceChanges: Array<{
    id: string;
    name: string;
    oldAmount: number;
    newAmount: number;
    changedAt: string;
  }>;
}

export interface SubscriptionCalendarDay {
  date: string;
  subscriptions: Array<{
    id: string;
    name: string;
    amount: number;
    currency: string;
    emoji?: string;
    frequency: string;
  }>;
}

export interface Subscription {
  _id: string;
  user: string;
  name?: string;
  merchantName: string;
  amount: number;
  currency: string;
  frequency: 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom';
  customIntervalDays?: number;
  category?: string;
  account?: string;
  nextBillingDate?: string;
  status: 'active' | 'paused' | 'cancelled' | 'detected' | 'expired';
  autoDetected: boolean;
  reminderDays: number;
  emoji?: string;
  notes?: string;
  startDate?: string;
  endDate?: string;
  billingDayOfMonth?: number;
  historyGenerationMode?: 'auto' | 'draft';
  transactionDefaults?: {
    description?: string;
    tags?: string[];
    merchant?: string;
    notes?: string;
    emoji?: string;
    type?: 'expense' | 'income';
    needsReview?: boolean;
  };
  templateId?: string;
  priceHistory: Array<{
    amount: number;
    date: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

// ─── Transaction Templates ────────────────────────────────────────────────────

export interface TransactionTemplate {
  _id: string;
  user: string;
  name: string;
  description?: string;
  type?: 'income' | 'expense';
  amount?: number;
  account?: string | { _id: string; name: string };
  category?: string | { _id: string; name: string; icon?: string };
  currency?: string;
  tags: string[];
  merchant?: string;
  emoji?: string;
  notes?: string;
  needsReview: boolean;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

// ─── Transaction Rule Types ───────────────────────────────────────────────────

/**
 * App-side rule operator vocabulary. Mirrors the backend validator/evaluator
 * exactly (see backend/src/validators/transactionRuleValidator.ts and
 * backend/src/handlers/transactionRuleHandler.ts#evaluateCondition). Keep in
 * sync with {@link RuleWireOperator}; the two unions stay structurally
 * identical on purpose so the read-side normalizer can pass values through.
 */
export type RuleOperator =
  | 'equals'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'regex'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between';

/**
 * Transport-only wire vocabulary. Appears on the read path (fetch/create/update
 * responses) and in persisted model/seed JSON. The wire payload may also carry
 * numeric `value` / `value2` fields which the read-side normalizer coerces to
 * strings (see frontend/src/lib/ruleConditions.ts).
 *
 * Declared as the explicit ten-value literal union rather than aliasing
 * {@link RuleOperator} so the wire contract stays a separately-auditable shape.
 * Both unions MUST stay in sync; divergence is a transport contract defect.
 */
export type RuleWireOperator =
  | 'equals'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'regex'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between';

/** Single wire condition as the backend returns it. */
export interface RuleConditionWire {
  field: string;
  operator: RuleWireOperator;
  value: string | number;
  value2?: number;
}

/** Nested wire group. `groups` is one level deep per current backend validator. */
export interface RuleConditionGroupWire {
  operator: 'AND' | 'OR';
  rules: RuleConditionWire[];
  groups?: RuleConditionGroupWire[];
}

/** The full wire union accepted by the backend (flat array OR one nested group). */
export type RuleConditionsWire = RuleConditionWire[] | RuleConditionGroupWire;

/** A whole rule whose `conditions` field is the wire union (not the flat app shape). */
export type TransactionRuleWire = Omit<TransactionRule, 'conditions'> & {
  conditions: RuleConditionsWire;
};

/** App-side flat condition. `value` is always a string; `between` uses "min,max". */
export interface RuleCondition {
  field: string;
  operator: RuleOperator;
  value: string;
}

/**
 * Builder-local nested group. Preserved separately from {@link RuleCondition}
 * so visual builders can collect user-typed conditions before the read-side
 * normalizer or a future save path converts them to the wire union.
 */
export interface RuleConditionGroup {
  logic: 'AND' | 'OR';
  conditions: RuleCondition[];
  groups: RuleConditionGroup[];
}

export interface RuleAction {
  type: 'setCategory' | 'setType' | 'addTag' | 'setDescription' | 'markReviewed' | 'setStatus' | 'setCurrency';
  value: string | boolean;
}

export interface TransactionRule {
  _id: string;
  user: string;
  name: string;
  conditions: RuleCondition[];
  conditionLogic: 'AND' | 'OR';
  actions: RuleAction[];
  isActive: boolean;
  priority: number;
  matchCount: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Rule Apply / Preview Types (T155P4) ─────────────────────────────────────

export interface RulePreviewPatchable {
  category: string | null;
  tags: string[];
  type: 'income' | 'expense' | 'transfer';
  emoji: string | null;
  currency: string;
}

export interface RulePreviewRow {
  transactionId: string;
  description: string;
  amount: number;
  date: string;
  merchant: string | null;
  before: RulePreviewPatchable;
  after: RulePreviewPatchable;
  reason: string;
  confidence: number;
}

export interface RuleDryRunResult {
  dryRun: true;
  /** Only present for single-rule apply */
  matchCount?: number;
  matches?: string[];
  appliedCount: number;
  previewCount: number;
  hasMorePreview: boolean;
  hasMoreChangedIds: boolean;
  preview: RulePreviewRow[];
  changedTransactionIds: string[];
}

export interface RuleCommitResult {
  applied?: number;
  batchId: string;
  changedCount: number;
  hasMoreChangedIds: boolean;
  changedTransactionIds: string[];
}

export interface RuleUndoResult {
  batchId: string;
  requestedCount: number;
  revertedCount: number;
  notFoundCount: number;
  failedCount: number;
  status: 'complete' | 'partial';
  revertedTransactionIds: string[];
}

// ─── Notification Types ───────────────────────────────────────────────────────

export interface Notification {
  _id: string;
  user: string;
  type: 'budget_alert' | 'bill_reminder' | 'goal_milestone' | 'anomaly' | 'subscription_change' | 'system' | 'sweep_suggestion';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  channel: 'in_app' | 'email' | 'push';
  read: boolean;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ─── Currency Types ───────────────────────────────────────────────────────────

export interface ExchangeRates {
  base: string;
  rates: Record<string, number>;
  timestamp: string;
}

export interface CurrencyConversion {
  from: string;
  to: string;
  amount: number;
  result: number;
  rate: number;
}

// ─── MFA Types ────────────────────────────────────────────────────────────────

export interface MFASetupData {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
  backupCodes: string[];
}

export interface MFAStatus {
  mfaEnabled: boolean;
  hasBackupCodes: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface AuthResponse {
  success: boolean;
  accessToken?: string; // JWT access token (short-lived)
  refreshToken?: string; // JWT refresh token (long-lived)
  token?: string; // Legacy field for backward compatibility
  user: User;
}

// ─── Extension System Types ───────────────────────────────────────────────────

export interface ExtensionAction {
  id: string;
  label: string;
  icon: string;
  page: string;
  handler: string;
  order: number;
}

export interface ExtensionWidget {
  id: string;
  label: string;
  icon: string;
  page: string;
  component: string;
  order: number;
}

export interface ExtensionImporter {
  id: string;
  label: string;
  icon: string;
  format: string;
  handler: string;
}

export interface ExtensionProcessor {
  id: string;
  label: string;
  trigger: string;
  handler: string;
}

export interface ExtensionContributes {
  actions: ExtensionAction[];
  widgets: ExtensionWidget[];
  importers: ExtensionImporter[];
  processors: ExtensionProcessor[];
}

export interface ExtensionSettings {
  schema: Record<string, unknown>;
  values: Record<string, unknown>;
}

export interface Extension {
  id: string;
  _id: string;
  extensionId: string;
  name: string;
  description: string;
  version: string;
  author: string;
  icon: string;
  type: 'system' | 'community';
  status: 'enabled' | 'disabled';
  contributes: ExtensionContributes;
  settings: ExtensionSettings;
  activationEvents: string[];
  userId: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Advanced Analytics Types (Analytics Overhaul) ────────────────────────────

export interface BudgetPerformanceCategory {
  categoryId: string;
  categoryName: string;
  budgeted: number;
  actual: number;
  percentage: number;
  status: 'under' | 'on_track' | 'over';
}

export interface BudgetPerformanceMonth {
  month: string;
  categories: BudgetPerformanceCategory[];
  totalBudgeted: number;
  totalActual: number;
}

export interface SpendingVelocityDay {
  day: number;
  cumulative: number;
}

export interface SpendingVelocityData {
  currentMonth: { month: string; days: SpendingVelocityDay[] };
  previousMonths: Array<{ month: string; days: SpendingVelocityDay[] }>;
  averageDailySpend: number;
  projectedMonthEnd: number;
}

export interface CategoryTreemapItem {
  categoryId: string;
  name: string;
  emoji?: string;
  amount: number;
  percentage: number;
  budgeted?: number;
  budgetUsage?: number;
  children?: Array<{
    categoryId: string;
    name: string;
    amount: number;
    percentage: number;
    budgeted?: number;
    budgetUsage?: number;
  }>;
}

export interface CategoryTreemapData {
  categories: CategoryTreemapItem[];
  totalExpenses: number;
}

export interface FinancialHealthScore {
  score: number;
  value: number;
  benchmark: number;
  label: string;
}

export interface FinancialHealthData {
  scores: Record<string, FinancialHealthScore>;
  overallScore: number;
  previousPeriodScore: number;
  trend: 'improving' | 'declining' | 'stable';
}

export interface ExpenseCalendarDay {
  date: string;
  amount: number;
  count: number;
}

export interface ExpenseCalendarData {
  days: ExpenseCalendarDay[];
  stats: { avgDaily: number; maxDaily: number; minDaily: number };
}

export interface TopMerchant {
  name: string;
  amount: number;
  count: number;
  avgTransaction: number;
  category?: string;
}

export interface RecurringVsDiscretionaryMonth {
  month: string;
  recurring: number;
  discretionary: number;
  total: number;
  income: number;
  recurringPercentage: number;
}

export interface DayOfWeekData {
  day: string;
  dayIndex: number;
  amount: number;
  count: number;
  avgAmount: number;
  topCategories: Array<{ name: string; amount: number }>;
}

// ── Items Analytics Types (T114-F4) ────────────────────────────────────
export interface ItemsSummaryCategory {
  category: string;
  categoryId?: string;
  categoryEmoji?: string;
  totalAmount: number;
  itemCount: number;
  items: Array<{ description: string; amount: number; count: number }>;
}

export interface ItemsFrequencyItem {
  description: string;
  purchaseCount: number;
  totalSpent: number;
  avgPrice: number;
  lastPurchased: string;
  category?: string;
  categoryEmoji?: string;
}

export interface ItemsSearchResult {
  transactionId: string;
  transactionDate: string;
  transactionDescription: string;
  merchant?: string;
  matchedItem: {
    description: string;
    amount: number;
    category?: string;
  };
}

// ── Batch Operations Types (T114-F5) ──────────────────────────────────
export interface BatchUpdatePayload {
  transactionIds: string[];
  updates: {
    category?: string;
    type?: 'income' | 'expense' | 'transfer';
    tags?: string[];
    emoji?: string;
    notes?: string;
    isTaxDeductible?: boolean;
  };
  dryRun?: boolean;
}

export interface BatchDeletePayload {
  transactionIds: string[];
  dryRun?: boolean;
}

export interface BatchApplyRulePayload {
  transactionIds: string[];
  ruleId: string;
  dryRun?: boolean;
}

export interface BatchTagPayload {
  transactionIds: string[];
  addTags?: string[];
  removeTags?: string[];
  dryRun?: boolean;
}

export interface BatchOperationResult {
  success: boolean;
  affected: number;
  total: number;
  results?: Array<{
    transactionId: string;
    description: string;
    amount: number;
    date: string;
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
    matched?: boolean;
    actions?: Array<{ type: string; value: string | boolean }>;
  }>;
  errors?: Array<{ transactionId: string; error: string }>;
}

// ── Dashboard Types (T113) ────────────────────────────────────
export const WIDGET_TYPES = [
  'kpi', 'line', 'area', 'bar', 'stacked-bar', 'donut',
  'radar', 'heatmap', 'progress', 'gauge', 'table', 'treemap',
  'configurable-trend',
] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];

// ── Configurable Trends Types ──
export type TrendGranularity = 'auto' | '1d' | '3d' | '1w' | '2w' | '1m' | '3m' | '6m' | '1y';
export type TrendSeriesId = 'income' | 'expenses' | 'netCashFlow' | 'savingsRate' | 'accountBalance' | 'categorySpend' | 'budgetUtilization';
export type TrendChartType = 'area' | 'bar' | 'line';

export interface TrendSeriesConfig {
  id: TrendSeriesId;
  enabled: boolean;
  color: string;
  label: string;
}

export interface TrendWidgetConfig {
  granularity: TrendGranularity;
  series: TrendSeriesConfig[];
  chartType: TrendChartType;
}

export const DATA_SOURCES = [
  'transactions', 'accounts', 'budgets', 'goals',
  'bills', 'subscriptions', 'snapshots', 'analytics',
] as const;
export type DataSource = (typeof DATA_SOURCES)[number];

export interface IWidgetDisplay {
  title?: string;
  icon?: string;
  showTitle?: boolean;
  visibleFields?: string[];
  /** [T149-PR8-D4] Glass-panel effect toggle — per-widget frosted-glass style */
  useGlassStyle?: boolean;
}

export interface WidgetCompatibilityFixCta {
  label: string;
  path: string;
}

export type WidgetCompatibilityReasonSeverity = 'error' | 'warn' | 'info';

export interface WidgetCompatibilityReason {
  code: string;
  message: string;
  severity: WidgetCompatibilityReasonSeverity;
}

export interface WidgetCompatibilityWarning {
  reason: string;
  fixCta: WidgetCompatibilityFixCta | null;
}

export interface WidgetCatalogItem {
  kind: string;
  category: string;
  title: string;
  description: string;
  minCellSize: {
    w: number;
    h: number;
  };
  previewDataAvailable: boolean;
}

export interface WidgetCompatibilityCheckResult {
  compatible: boolean;
  reasons?: WidgetCompatibilityReason[];
  severity?: WidgetCompatibilityReasonSeverity;
  warnings?: WidgetCompatibilityWarning[];
  fixCta: WidgetCompatibilityFixCta | null;
  /**
   * [T149-CONT-BE] Backend-canonical descriptor key after alias normalisation.
   * Equal to requestedKind when no alias normalisation occurred.
   * Use to normalise local widget identity on add/replace.
   */
  canonicalKind?: string;
  /**
   * [T149-CONT-BE] Raw kind string sent by the client in the check request.
   * Useful for detecting alias drift between local skin kinds and backend descriptors.
   */
  requestedKind?: string;
}

export interface IWidget {
  _id?: string;
  id: string;
  type: WidgetType;
  /** [T149] Canonical semantic kind — preferred over `type` for parity-path rendering.
   *  Set on new widgets created via the Custom Dashboard builder; absent on legacy widgets.
   *  When present, `WidgetRenderer` routes directly to the SKIN_REGISTRY adapter. */
  kind?: string;
  /**
   * [Phase25-L2] Canonical descriptor identity.
   * One-release bridge keeps `descriptorKey` populated for backward compatibility.
   */
  descriptorId?: string;
  descriptorKey?: string;
  title: string;
  display?: IWidgetDisplay;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  dataSource: DataSource;
  metric: string;
  dimensions?: string[];
  filters?: Record<string, unknown>;
  config?: Record<string, unknown>;
  customTitle?: string;
  hidden?: boolean;
  compatibilityWarnings?: WidgetCompatibilityWarning[];
}

export interface IDashboardFromTemplate {
  templateId?: string;
  version?: number;
  appliedAt?: string;
}

export interface IDashboardLayout {
  columns: number;
  rowHeight: number;
}

export interface IDashboardLayoutModeItem {
  widgetId: string;
  id?: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface IDashboardLayoutMode {
  columns: number;
  rowHeight: number;
  isCustom: boolean;
  updatedAt?: string;
  items: IDashboardLayoutModeItem[];
}

export interface IDashboardLayoutModes {
  desktop?: IDashboardLayoutMode;
  tablet?: IDashboardLayoutMode;
  mobile?: IDashboardLayoutMode;
}

export interface IDashboard {
  _id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  widgets: IWidget[];
  layout: IDashboardLayout;
  layoutModes?: IDashboardLayoutModes;
  emoji?: string;
  sourceTemplateId?: string;
  fromTemplate?: IDashboardFromTemplate;
  createdAt?: string;
  updatedAt?: string;
}

// ── Receipt Types (T114-F1) ───────────────────────────────────────────
export interface ReceiptItem {
  name: string;
  quantity?: number;
  price?: number;
}

export interface ReceiptMetadata {
  merchant?: string;
  date?: string;
  total?: number;
  currency?: string;
  category?: string;
  items?: ReceiptItem[];
  extractedText?: string;
}

export interface ReceiptSuggestedCategory {
  category: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

export interface Receipt {
  _id: string;
  userId: string;
  transactionId?: string;
  originalFilename: string;
  storagePath: string;
  storageProvider: 'local' | 'gcs' | 's3';
  mimeType: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  width?: number;
  height?: number;
  thumbnailPath?: string;
  metadata: ReceiptMetadata;
  suggestedCategory?: ReceiptSuggestedCategory;
  categorizedAt?: string;
  tags: string[];
  notes?: string;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReceiptShare {
  _id: string;
  receiptId: string;
  token: string;
  shareUrl?: string;
  expiresAt: string;
  maxAccesses?: number;
  accessCount: number;
  privacySettings: {
    includeAmount: boolean;
    includeItems: boolean;
    includeNotes: boolean;
    includeMerchant: boolean;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// T155P1: Review Cockpit types
// ---------------------------------------------------------------------------

/** A single bucket returned by GET /transactions/review-queue */
export interface ReviewBucket {
  count: number;
  items: Transaction[];
}

/** Response shape for GET /transactions/review-queue */
export interface ReviewQueue {
  uncategorized: ReviewBucket;
  suspicious: ReviewBucket;
  duplicateLike: ReviewBucket;
  recurringLinked: ReviewBucket;
  total: number;
}

/** Response shape for POST /transactions/batch/mark-reviewed */
export interface BatchMarkReviewedResult {
  modifiedCount: number;
  modifiedIds: string[];
}

// ── Cash-Flow Foresight Types (T155P2) ─────────────────────────────────
export interface ProjectedEvent {
  label: string;
  amount: number;
  type: 'income' | 'expense';
}

export interface ProjectedDay {
  date: string;
  projectedBalance: number;
  events: ProjectedEvent[];
}

export interface ProjectedBalanceData {
  startingBalance: number;
  projectedDays: ProjectedDay[];
  avgDailyBurn: number;
  avgMonthlyBurn: number;
  dataSourceMonths: number;
}
